import { useQuery } from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import { NddLocationMetrics, NddHierarchyMetrics, NddApiProps } from 'types/ndd'
import { TIME } from 'utils/misc'
import { TableData } from 'utils/data'
import {
    fetchNddLocationMetrics,
    fetchNddRegionMetrics,
    fetchNddGroupMetrics,
    fetchNddDistrictMetrics,
    fetchNddTargetMetrics,
    fetchNddHistoricalData,
} from 'queries/ndd/ndd-metrics'
import { isDistrict, isRegion, isGroup, isCompany } from 'utils/location'
import { Constants } from '../../constants/constants'
import TIME_PERIOD_KEYS = Constants.TIME_PERIOD_KEYS
import { MPMEnvType } from 'utils/env'

const { tenMinutes } = TIME

interface UseNddStoreMetricsProps {
    env: MPMEnvType
    accessToken: string
    location: string
}

interface UseNddHierarchyMetricsProps {
    env: MPMEnvType
    accessToken: string
    location: string
}

interface UseNddStoreMetricsByTimePeriodProps {
    env: MPMEnvType
    accessToken: string
    location: string
    timeDimension: string
}

export function useNddStoreMetrics({
    env,
    accessToken,
    location,
}: UseNddStoreMetricsProps) {
    const numericLocation = location.replace(/\D/g, '')

    const fetchFunction = async (): Promise<NddLocationMetrics> => {
        const apiProps: NddApiProps = {
            env,
            accessToken,
            locationId: numericLocation,
        }
        return await fetchNddLocationMetrics(apiProps)
    }

    const { data, isLoading, isError, refetch, remove, error } =
        useQuery<NddLocationMetrics>(
            [QueryKeys.NDD_LOCATION_METRICS, numericLocation],
            fetchFunction,
            {
                staleTime: 0,
                refetchInterval: tenMinutes,
                retry: false,
            },
        )

    return {
        data,
        isLoading,
        isError,
        refetch,
        remove,
        error,
    }
}

export function useNddHierarchyMetrics({
    env,
    accessToken,
    location,
}: UseNddHierarchyMetricsProps) {
    const numericLocation = location.replace(/\D/g, '')

    const fetchFunction = async (): Promise<NddHierarchyMetrics> => {
        const apiProps: NddApiProps = {
            env,
            accessToken,
            locationId: numericLocation,
        }

        switch (true) {
            case isDistrict(location):
                return await fetchNddDistrictMetrics(apiProps)
            case isRegion(location):
                return await fetchNddRegionMetrics(apiProps)
            case isGroup(location):
                return await fetchNddGroupMetrics(apiProps)
            case isCompany(location):
                return await fetchNddTargetMetrics({ env, accessToken })
            default:
                return await fetchNddDistrictMetrics(apiProps)
        }
    }

    const { data, isLoading, isError, refetch, remove, error } =
        useQuery<NddHierarchyMetrics>(
            [QueryKeys.NDD_HIERARCHY_METRICS, numericLocation],
            fetchFunction,
            {
                staleTime: 0,
                refetchInterval: tenMinutes,
                retry: false,
            },
        )

    return {
        data,
        isLoading,
        isError,
        refetch,
        remove,
        error,
    }
}

export function useHistoricalNddStoreMetrics({
    env,
    accessToken,
    location,
    timeDimension,
}: UseNddStoreMetricsByTimePeriodProps) {
    const filters: Record<string, string> = {
        time_dimension: timeDimension,
        location_id: location,
    }

    const fetchHistorical = async (): Promise<TableData> => {
        const result = await fetchNddHistoricalData({
            env,
            accessToken,
            filters,
            dynamicSubPath:
                Constants.GREENFIELD_CARDS.FULFILLMENT.NDD_HISTORICAL,
        })
        return result as TableData
    }

    const enabled =
        !!timeDimension &&
        timeDimension !== TIME_PERIOD_KEYS.TODAY &&
        !!env &&
        !!accessToken &&
        !!location

    const { data, isLoading, isError, refetch, remove, error } =
        useQuery<TableData>(
            [QueryKeys.NDD_HISTORICAL_METRICS, location, timeDimension],
            fetchHistorical,
            {
                staleTime: tenMinutes,
                cacheTime: tenMinutes,
                retry: false,
                enabled,
            },
        )

    return { data, isLoading, isError, refetch, remove, error }
}
